import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Plus, FileText, Sparkles, ShieldCheck, Send, ChevronLeft, Download, Loader2, CheckCircle2, History, GraduationCap, RefreshCw } from "lucide-react";
import api, { API } from "../lib/api";
import { PageHeader, Loading, EmptyState, AITag, ApprovedTag } from "../components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-600", approved: "bg-emerald-100 text-emerald-700",
  sent: "bg-sky-100 text-sky-700", accepted: "bg-teal-100 text-teal-700",
};

export default function Proposals() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: list, isLoading } = useQuery({ queryKey: ["proposals"], queryFn: async () => (await api.get("/proposals")).data });

  if (isLoading) return <Loading />;
  if (selected) return <ProposalEditor id={selected} onBack={() => setSelected(null)} />;

  return (
    <div>
      <PageHeader eyebrow="Proposal Builder" title="Proposals"
        description="Draft proposals from a versioned template with AI-generated sections you review and approve. Export to PDF or DOCX and mark as sent."
        actions={<Button onClick={() => setCreateOpen(true)} data-testid="new-proposal" className="bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4 mr-1.5" /> New proposal</Button>} />
      {(list || []).length === 0 ? (
        <EmptyState icon={FileText} title="No proposals yet" description="Create a proposal from an opportunity or diagnostic." action={<Button onClick={() => setCreateOpen(true)} className="bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4 mr-1.5" /> New proposal</Button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((p) => (
            <button key={p.id} onClick={() => setSelected(p.id)} data-testid={`proposal-card-${p.id}`}
              className="text-left bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md hover:border-emerald-200 p-6 transition-all">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3"><FileText className="w-5 h-5" /></div>
              <div className="font-display font-semibold text-slate-900 leading-snug">{p.title}</div>
              <div className="text-sm text-slate-500 mt-0.5">{p.org_name}</div>
              <div className="flex items-center gap-2 mt-3">
                <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_STYLES[p.status]}`}>{p.status}</span>
                <span className="text-xs text-slate-400 font-mono">v{p.version}</span>
              </div>
            </button>
          ))}
        </div>
      )}
      <CreateProposalDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(id) => { qc.invalidateQueries({ queryKey: ["proposals"] }); setCreateOpen(false); setSelected(id); }} />
    </div>
  );
}

function CreateProposalDialog({ open, onClose, onCreated }) {
  const { data: opps } = useQuery({ queryKey: ["opps"], queryFn: async () => (await api.get("/crm/opportunities")).data, enabled: open });
  const { data: diags } = useQuery({ queryKey: ["diagnostics"], queryFn: async () => (await api.get("/diagnostic")).data, enabled: open });
  const { data: templates } = useQuery({ queryKey: ["proposal-templates"], queryFn: async () => (await api.get("/proposals/templates")).data, enabled: open });
  const [title, setTitle] = useState("");
  const [orgName, setOrgName] = useState("");
  const [oppId, setOppId] = useState("");
  const [diagId, setDiagId] = useState("");
  const [templateKey, setTemplateKey] = useState("standard");

  const create = useMutation({ mutationFn: () => api.post("/proposals", { title, org_name: orgName, opportunity_id: oppId || null, diagnostic_id: diagId || null, template_key: templateKey }), onSuccess: (r) => { onCreated(r.data.id); setTitle(""); setOrgName(""); setOppId(""); setDiagId(""); setTemplateKey("standard"); } });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>New proposal</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Template</Label>
            <Select value={templateKey} onValueChange={setTemplateKey}>
              <SelectTrigger className="mt-1.5" data-testid="proposal-template"><SelectValue /></SelectTrigger>
              <SelectContent>{(templates || []).map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
            {templateKey === "sgx_issb" && <p className="text-xs text-emerald-600 mt-1.5">Singapore-ready: sections aligned to SGX Listing Rules & ISSB IFRS S1/S2.</p>}
          </div>
          <div><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="proposal-title" className="mt-1.5" placeholder="Sustainability Advisory Proposal" /></div>
          <div><Label>Client name</Label><Input value={orgName} onChange={(e) => setOrgName(e.target.value)} data-testid="proposal-org" className="mt-1.5" /></div>
          <div><Label>Link opportunity <span className="text-slate-400">(optional)</span></Label>
            <Select value={oppId} onValueChange={(v) => { setOppId(v); const o = opps?.find((x) => x.id === v); if (o && !orgName) setOrgName(o.org_name); if (o && !title) setTitle(`${o.org_name} — Proposal`); }}>
              <SelectTrigger className="mt-1.5" data-testid="proposal-opp"><SelectValue placeholder="Select opportunity" /></SelectTrigger>
              <SelectContent>{(opps || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Link diagnostic <span className="text-slate-400">(optional, powers AI drafting)</span></Label>
            <Select value={diagId} onValueChange={setDiagId}>
              <SelectTrigger className="mt-1.5" data-testid="proposal-diag"><SelectValue placeholder="Select diagnostic" /></SelectTrigger>
              <SelectContent>{(diags || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.org_name} v{d.version} ({d.scoring.overall_score}%)</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => create.mutate()} disabled={!title || !orgName || create.isPending} data-testid="proposal-create-confirm">{create.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProposalEditor({ id, onBack }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data: p, isLoading } = useQuery({ queryKey: ["proposal", id], queryFn: async () => (await api.get(`/proposals/${id}`)).data });
  const [sections, setSections] = useState(null);
  const [showVersions, setShowVersions] = useState(false);

  const cur = sections ?? p?.sections ?? [];
  const setContent = (i, content) => setSections(cur.map((s, idx) => idx === i ? { ...s, content, ai_generated: false } : s));

  const aiDraft = useMutation({ mutationFn: () => api.post(`/proposals/${id}/ai-draft`), onSuccess: (r) => { qc.setQueryData(["proposal", id], r.data); setSections(r.data.sections); toast.success("AI drafted all sections — review & edit"); } });
  const saveMut = useMutation({ mutationFn: () => api.put(`/proposals/${id}`, { sections: cur }), onSuccess: (r) => { qc.setQueryData(["proposal", id], r.data); setSections(null); toast.success("Saved"); } });
  const snapshot = useMutation({ mutationFn: () => api.post(`/proposals/${id}/version`), onSuccess: (r) => { qc.setQueryData(["proposal", id], r.data); toast.success(`Snapshotted v${r.data.version - 1}`); } });
  const approve = useMutation({ mutationFn: () => api.post(`/proposals/${id}/approve`), onSuccess: (r) => { qc.setQueryData(["proposal", id], r.data); qc.invalidateQueries({ queryKey: ["proposals"] }); toast.success("Proposal approved"); } });
  const send = useMutation({ mutationFn: () => api.post(`/proposals/${id}/send`), onSuccess: (r) => { qc.setQueryData(["proposal", id], r.data); toast.success("Marked as sent"); } });
  const accept = useMutation({ mutationFn: () => api.post(`/proposals/${id}/accept`), onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["proposals"] }); toast.success("Accepted — engagement created"); nav("/engagements"); } });
  const refreshComp = useMutation({ mutationFn: () => api.post(`/proposals/${id}/competencies/refresh`), onSuccess: (r) => { qc.setQueryData(["proposal", id], r.data); setSections(null); toast.success("Adviser Competencies refreshed from your Academy profile"); } });

  if (isLoading || !p) return <Loading />;
  const locked = p.status === "sent" || p.status === "accepted";

  return (
    <div className="max-w-3xl">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"><ChevronLeft className="w-4 h-4" /> Back to proposals</button>
      <PageHeader eyebrow={`${p.org_name} · ${p.template_name || p.template_version} · v${p.version}`} title={p.title}
        actions={<span className={`text-xs px-2.5 py-1 rounded-full capitalize font-medium ${STATUS_STYLES[p.status]}`}>{p.status}</span>} />

      <div className="flex flex-wrap gap-2 mb-6">
        {!locked && <Button variant="outline" onClick={() => aiDraft.mutate()} disabled={aiDraft.isPending} data-testid="ai-draft-proposal">{aiDraft.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5 text-emerald-600" />} AI draft all sections</Button>}
        <Button variant="outline" onClick={() => setShowVersions(true)} data-testid="proposal-versions"><History className="w-4 h-4 mr-1.5" /> Versions ({p.versions?.length || 0})</Button>
        <a href={`${API}/proposals/${id}/export/pdf`} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="export-pdf"><Download className="w-4 h-4 mr-1.5" /> PDF</Button></a>
        <a href={`${API}/proposals/${id}/export/docx`} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="export-docx"><Download className="w-4 h-4 mr-1.5" /> DOCX</Button></a>
      </div>

      <div className="space-y-4">
        {cur.map((s, i) => (
          <div key={s.id} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5" data-testid={`section-${s.heading.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <h3 className="font-display font-semibold text-slate-900">{s.heading}</h3>
              <div className="flex items-center gap-2">
                {s.ai_generated && <AITag />}
                {s.system_generated && (
                  <>
                    <span data-testid="competency-section-tag" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-800 border border-emerald-200/80"><GraduationCap className="w-3 h-3" /> From your Academy profile</span>
                    {!locked && <Button size="sm" variant="ghost" onClick={() => refreshComp.mutate()} disabled={refreshComp.isPending} data-testid="refresh-competencies" className="h-7 text-xs text-slate-600"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshComp.isPending ? "animate-spin" : ""}`} /> Refresh</Button>}
                  </>
                )}
              </div>
            </div>
            <Textarea value={s.content} onChange={(e) => setContent(i, e.target.value)} disabled={locked} className="min-h-[90px] text-sm leading-relaxed" placeholder={`Write the ${s.heading.toLowerCase()} section…`} />
          </div>
        ))}
      </div>

      <div className="flex flex-wrap justify-end gap-2 mt-6 sticky bottom-4 bg-slate-50/80 backdrop-blur py-2 rounded-xl">
        {sections && !locked && <Button variant="outline" onClick={() => saveMut.mutate()} data-testid="save-proposal">Save changes</Button>}
        {!locked && <Button variant="outline" onClick={() => snapshot.mutate()}>Snapshot version</Button>}
        {!p.approved && !locked && <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => approve.mutate()} data-testid="approve-proposal"><ShieldCheck className="w-4 h-4 mr-1.5" /> Approve</Button>}
        {p.status === "approved" && <Button className="bg-sky-600 hover:bg-sky-700" onClick={() => send.mutate()} data-testid="send-proposal"><Send className="w-4 h-4 mr-1.5" /> Mark as sent</Button>}
        {p.status === "sent" && <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => accept.mutate()} data-testid="accept-proposal"><CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark accepted → create engagement</Button>}
        {p.status === "accepted" && <ApprovedTag label="Accepted — engagement created" />}
      </div>

      <Dialog open={showVersions} onOpenChange={setShowVersions}>
        <DialogContent>
          <DialogHeader><DialogTitle>Version history</DialogTitle></DialogHeader>
          {(p.versions || []).length === 0 ? <p className="text-sm text-slate-400">No snapshots yet. Snapshot a version to preserve the current draft.</p> : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {p.versions.map((v, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <span className="text-sm text-slate-700">Version {v.version}</span>
                  <span className="text-xs text-slate-400 font-mono">{new Date(v.snapshot_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
